import React, { useRef, useEffect, useMemo } from 'react'
import Loader from 'components/Loader'
import { useLocation, useHistory, useRouteMatch } from 'react-router'
import CardLayout from 'routes/Card/Layout'
import { useSelector } from 'react-redux'
import { Modal, navigationController } from 'components/Modal'
import CoreLayout from 'layouts/CoreLayout'
import { ModalProvider } from 'components/Modal'
import { Route, Switch, Redirect } from 'react-router-dom'
import { Location } from 'history'
import { useCardDataProvider } from 'dataProviders/Card'
import Layout from './Layout'

const LazyBoard = React.lazy(() => import('./Board'))

const routes = [
  {
    path: ['/userGroups', '/userGroups/:id(\\d+)'],
    layout: React.lazy(() => import('./UserGroups'))
  },
  {
    path: '/myboard/:userId(\\d+)',
    layout: React.lazy(() => import('./MyBoard'))
  },
  {
    path: '/search',
    layout: React.lazy(() => import('./Search'))
  },
  {
    path: ['/home/:id(\\d+)', '/home/boards', '/'],
    exact: true,
    layout: React.lazy(() => import('./Home'))
  },
]

const getId = (loc) => +loc.split('-')[1]

const NoMatch = ({ match }) => {
  const url = match.url

  if (/^\/?board-[0-9]+$/i.test(url)) { // Board
    return <Redirect to={'/board/' + getId(url)} />
  } else if (/^\/?myboard-[0-9]+$/i.test(url)) { // MyBoard
    return <Redirect to={'/myboard/' + getId(url)} />
  } else if (/^\/?card-[0-9]+$/i.test(url)) { // Card
    return <Redirect to={'/card/' + getId(url)} />
  } else if (/^\/?view-board-[0-9]+,card-[0-9]+$/i.test(url)) { // Old card link format
    return <Redirect to={'/card/' + getId(url.split(',')[1])} />
  } else if (/^\/?board-[0-9]+,card-[0-9]+$/i.test(url)) { // Old card link format
    return <Redirect to={'/card/' + getId(url.split(',')[1])} />
  } else {
    return <Redirect to='/' />
  }
}

type State = {
  cards?: any[]
  fromLocation?: Location<State>
}

const CardModal = ({ cardId, location, onClose, isActive }) => {
  useCardDataProvider(cardId)

  return (
    <Modal
      cardId={cardId}
      position='center'
      onClose={onClose}
    >
      <CardLayout id={cardId} location={location} isActive={isActive} />
    </Modal>
  )
}

const Routes: React.FC = () => {
  const history = useHistory()
  const location = useLocation<State>()

  const cardMatch = useRouteMatch<{ cardId: string }>('/card/:cardId(\\d+)')
  
  const cardId = Number(cardMatch?.params.cardId)

  const cardBoardId = useSelector(state => cardId && state['view-board'].cards[cardId]?.boardId)

  useEffect(() => {
    // If route was opened with card url, then open this card's board in the background
    if (!location.state?.fromLocation && cardId && cardBoardId) {
      history.replace({
        ...location,
        pathname: `/board/${cardBoardId}`,
      })

      history.push({
        ...location,
        state: {
          ...location.state,
          fromLocation: {
            pathname: `/board/${cardBoardId}`,
          }
        }
      })
    }
  }, [location, cardId, cardBoardId])

  const cards = useMemo(() => {
    if (cardId) {
      return [
        ...(location.state?.cards || []),
        {
          id: cardId,
          locationKey: location.key,
        },
      ]
    }

    return []
  }, [cardId, location])

  const closeRef = useRef((id, key) => {})

  closeRef.current = (id, key) => {
    const checkCard = cards.find(checkCard => checkCard.locationKey === key)
    if (checkCard) {
      history.goBack()
    }
  }

  const fromLocation = useMemo(() => {
    if (location.state?.fromLocation) return location.state.fromLocation

    return location
  }, [location])

  const { filter } = navigationController.useActions()
  useEffect(() => {
    filter(view => Boolean(view.cardId))
  }, [fromLocation.pathname])

  return (
    <Layout>
      <React.Suspense fallback={<CoreLayout><Loader absoluteCenter /></CoreLayout>}>
        <Switch
          location={fromLocation}
        >
          <Route
            exact
            path={['/board/:boardId(\\d+)', '/board/:boardId(\\d+)/:viewId']}
          >
            <LazyBoard
              currentLocation={location}
            />
          </Route>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              exact={route.exact}
              component={route.layout}
            />
          ))}
          <Route path={'/card/:cardId(\\d+)'} />
          <Route component={NoMatch} />
        </Switch>
      </React.Suspense>
      {cards.map((card, index) =>
        <CardModal
          key={`${card.id}-${card.locationKey}`}
          cardId={card.id}
          onClose={() => {
            closeRef.current(card.id, card.locationKey)
          }}
          location={location}
          isActive={cards.length - 1 === index}
        />
      )}
    </Layout>
  )
}

const RoutesWithModalProvider = () => {
  return (
    <ModalProvider>
      <Routes />
    </ModalProvider>
  )
}

export default RoutesWithModalProvider